// public/js/services/analysis/RecommendationMethods.js
export const RecommendationMethods = {
    identifyHighPotentialGames(games) {
        return games
            .filter(game =>
                this.isRising(game) &&
                this.hasPotential(game) &&
                game.reviewScore > 80
            )
            .map(game => ({
                gameId: game.steamId,
                name: game.name,
                reason: "High growth potential with strong reviews",
                metrics: {
                    growth: this.analyzeWishlistTrends(game).growth,
                    reviewScore: game.reviewScore
                }
            }));
    },

    identifyGamesNeedingAttention(games) {
        return games
            .filter(game => {
                const trends = this.analyzeWishlistTrends(game);
                const metrics = this.calculateGrowthMetrics(game);
                return trends.growth < 0 || (metrics?.revenueGrowth || 0) < 0;
            })
            .map(game => ({
                gameId: game.steamId,
                name: game.name,
                reason: "Declining trends",
                metrics: {
                    wishlistGrowth: this.analyzeWishlistTrends(game).growth,
                    revenueGrowth: this.calculateGrowthMetrics(game)?.revenueGrowth
                }
            }));
    },

    generatePricingRecommendations(games) {
        return games.map(game => {
            const position = this.analyzePricePosition(game);
            const metrics = this.calculateGrowthMetrics(game);

            let recommendation = null;
            let confidence = 'medium';

            if (!position.isCompetitive && metrics?.revenueGrowth < 0) {
                recommendation = 'Consider price reduction';
                confidence = 'high';
            } else if (position.isCompetitive && game.reviewScore > 85 && game.price < position.competitorAverage) {
                recommendation = 'Potential for price increase';
                confidence = 'medium';
            } else if (game.price === 0 && game.reviewScore > 70) {
                recommendation = 'Consider monetization';
                confidence = 'medium';
            }

            return {
                gameId: game.steamId,
                name: game.name,
                currentPrice: game.price,
                recommendation,
                confidence,
                competitorAverage: position.competitorAverage
            };
        }).filter(rec => rec.recommendation !== null);
    },

    generateMarketingRecommendations(games) {
        return games.map(game => {
            const trends = this.analyzeWishlistTrends(game);
            const position = this.assessMarketPosition(game);

            const recommendations = [];

            if (trends.growth < 0.05) {
                recommendations.push('Increase visibility');
            }

            if (position.opportunities.includes('Market Expansion')) {
                recommendations.push('Target new regions');
            }

            if (game.tags?.length < 5) {
                recommendations.push('Optimize store tags');
            }

            if (recommendations.length === 0) return null;

            return {
                gameId: game.steamId,
                name: game.name,
                recommendations,
                priorities: this.prioritizeRecommendations(recommendations, game)
            };
        }).filter(Boolean);
    },

    generateDevelopmentRecommendations(games) {
        return games.map(game => {
            const metrics = this.calculateGrowthMetrics(game);
            const position = this.assessMarketPosition(game);

            const recommendations = [];

            if (this.needsMoreContent(game)) {
                recommendations.push('Add more content');
            }

            const featureGaps = this.identifyFeatureGaps(game);
            if (featureGaps.length > 0) {
                recommendations.push(`Add missing features: ${featureGaps.join(', ')}`);
            }

            if (metrics?.playerGrowth < 0) {
                recommendations.push('Improve player retention');
            }

            if (recommendations.length === 0) return null;

            return {
                gameId: game.steamId,
                name: game.name,
                recommendations,
                impact: this.estimateDevImpact(recommendations, game)
            };
        }).filter(Boolean);
    },

    prioritizeRecommendations(recommendations, game) {
        return recommendations.map(rec => ({
            action: rec,
            priority: this.calculateRecommendationPriority(rec, game),
            estimatedImpact: this.estimateRecommendationImpact(rec, game)
        })).sort((a, b) => b.priority - a.priority);
    }
};
